import { apiFetch, apiFetchBlob, apiSend, apiSendForBlob, apiSendForm, apiSendJson } from './client';
import type {
  CreateQrCodeRequest,
  QrBackground,
  QrCode,
  QrCodeAuditEntry,
  QrCodeStats,
  QrForeground,
  QrLogoStyle,
  QrImageFormat,
  ScanEvent,
  UpdateQrCodeRequest,
  VCardContact,
} from './types';

export interface QrCodeListFilter {
  groupId?: string;
  search?: string;
  /** Archived codes are retired, so they are left out unless asked for. */
  includeArchived?: boolean;
}

function queryString(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }

  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
}

export function listQrCodes(filter: QrCodeListFilter = {}): Promise<QrCode[]> {
  const query = queryString({
    groupId: filter.groupId,
    search: filter.search,
    includeArchived: filter.includeArchived ? true : undefined,
  });

  return apiFetch<QrCode[]>(`/api/qr-codes${query}`);
}

export function getQrCode(id: string): Promise<QrCode> {
  return apiFetch<QrCode>(`/api/qr-codes/${id}`);
}

/** A code's history, newest first. */
export function getQrCodeAudit(id: string): Promise<QrCodeAuditEntry[]> {
  return apiFetch<QrCodeAuditEntry[]>(`/api/qr-codes/${id}/audit`);
}

export function createQrCode(request: CreateQrCodeRequest): Promise<QrCode> {
  return apiSendJson<QrCode>('/api/qr-codes', 'POST', request);
}

/** A link code pointing at the card it carries, created in the caller's personal space. */
export function createContactQrCode(
  contact: VCardContact,
  entraUserId?: string | null,
  entraSynced = false,
): Promise<QrCode> {
  return apiSendJson<QrCode>('/api/qr-codes/contact', 'POST', {
    contact,
    entraUserId: entraUserId ?? undefined,
    entraSynced,
  });
}

/** Rewrites the card a contact code serves; the printed image does not change. */
export function updateQrCodeContact(id: string, contact: VCardContact, entraSynced = false): Promise<QrCode> {
  const query = entraSynced ? '?entraSynced=true' : '';
  return apiSendJson<QrCode>(`/api/qr-codes/${id}/contact${query}`, 'PUT', contact);
}

export function getQrCodeContactPhoto(id: string): Promise<Blob> {
  return apiFetchBlob(`/api/qr-codes/${id}/contact/photo`);
}

export interface CreateFileQrCodeRequest {
  ownerGroupId: string;
  title: string;
  slug?: string;
  file: File;
}

/** A file code is created together with its first file, which the API checks for viruses before serving it. */
export function createFileQrCode(request: CreateFileQrCodeRequest): Promise<QrCode> {
  const form = new FormData();
  form.set('ownerGroupId', request.ownerGroupId);
  form.set('title', request.title);
  if (request.slug) {
    form.set('slug', request.slug);
  }
  form.set('file', request.file);
  return apiSendForm<QrCode>('/api/qr-codes/file', form);
}

/** Uploads a new version. Scans keep getting the current one until the new one is cleared. */
export function replaceQrCodeFile(id: string, file: File): Promise<QrCode> {
  const form = new FormData();
  form.set('file', file);
  return apiFetch<QrCode>(`/api/qr-codes/${id}/file`, { method: 'PUT', body: form });
}

export function downloadQrCodeFile(id: string, fileId: string): Promise<Blob> {
  return apiFetchBlob(`/api/qr-codes/${id}/files/${fileId}`);
}

export function updateQrCode(id: string, request: UpdateQrCodeRequest): Promise<QrCode> {
  return apiSendJson<QrCode>(`/api/qr-codes/${id}`, 'PUT', request);
}

/**
 * Retires a code: what everybody with edit access has instead of deleting. It stops redirecting and leaves the
 * list, but it and its statistics are kept, and it can be restored.
 */
export function archiveQrCode(id: string): Promise<QrCode> {
  return apiSendJson<QrCode>(`/api/qr-codes/${id}/archive`, 'POST');
}

export function restoreQrCode(id: string): Promise<QrCode> {
  return apiSendJson<QrCode>(`/api/qr-codes/${id}/restore`, 'POST');
}

/**
 * Destroys a code and its history. Super admins only, and the interface warns before calling it: the link
 * identifier goes back into circulation, so a future code could inherit it and repoint every printed copy.
 */
export function deleteQrCode(id: string): Promise<void> {
  return apiSend(`/api/qr-codes/${id}`, 'DELETE');
}

export function getQrCodeStats(id: string, days = 30): Promise<QrCodeStats> {
  return apiFetch<QrCodeStats>(`/api/qr-codes/${id}/stats${queryString({ days })}`);
}

export function getQrCodeScans(id: string, take = 25): Promise<ScanEvent[]> {
  return apiFetch<ScanEvent[]>(`/api/qr-codes/${id}/scans${queryString({ take })}`);
}

/**
 * The image endpoint is authenticated, so it cannot be used directly as an `<img src>`: fetch the bytes
 * with the bearer token and hand back a blob the caller turns into an object URL.
 */
export function getQrCodeImage(
  id: string,
  format: QrImageFormat = 'png',
  size = 10,
  foreground?: QrForeground,
  background?: QrBackground,
  logoStyle?: QrLogoStyle,
): Promise<Blob> {
  const query = queryString({ format, size, foreground, background, logoStyle });
  return apiFetchBlob(`/api/qr-codes/${id}/image${query}`);
}

export const MAX_BATCH_IMAGES = 500;

export const DOWNLOAD_SIZE: Record<QrImageFormat, number> = { png: 20, svg: 10 };

export type QrCodeImageBatchSource = { ids: string[] } | { groupId: string };

export interface QrCodeImageBatchOptions {
  format: QrImageFormat;
  size: number;
  foreground?: QrForeground;
  background?: QrBackground;
  logoStyle?: QrLogoStyle;
}

export function downloadQrCodeImages(
  source: QrCodeImageBatchSource,
  options: QrCodeImageBatchOptions,
): Promise<Blob> {
  return apiSendForBlob('/api/qr-codes/images', 'POST', { ...source, ...options });
}
